import { createAsyncThunk } from '@reduxjs/toolkit';
import { createCrudThunks } from '../../store/createCrudThunks';
import {
  messageApi,
  type MessageListParams,
  type MessageCreatePayload,
  type MessageUpdatePayload,
} from './messageApi';
import { messageInfoApi } from './messageInfoApi';
import { extractError } from '../../store/thunkHelpers';
import { mockAiAnalyses } from '../../api/mocks/messageMocks';
import type { Message, MessageSummary } from './messageTypes';
import type { MessageInfo, MessageInfoUpdatePayload } from './messageInfoTypes';

const thunks = createCrudThunks<Message, MessageCreatePayload, MessageUpdatePayload, MessageListParams>(
  'messages',
  messageApi,
);

export const messageThunks = thunks;
export const {
  fetchAll: fetchMessages,
  fetchOne: fetchMessage,
  create: createMessage,
  update: updateMessage,
  remove: deleteMessage,
} = thunks;

/**
 * Fetch della proiezione LEGGERA di un Message (GET /message/{id}/summary), per la timeline
 * attività (sezione Email). Popola `state.messages.summariesById`, separata da `byId` (Message
 * pieno usato dalla smart inbox). Economico: niente join eager lato backend.
 */
export const fetchMessageSummary = createAsyncThunk<
  MessageSummary,
  number,
  { rejectValue: string }
>('messages/fetchSummary', async (messageId, { rejectWithValue }) => {
  try {
    return await messageApi.getSummary(messageId);
  } catch (err) {
    return rejectWithValue(extractError(err));
  }
});

/**
 * Aggiorna value/priority di una MessageInfo via PATCH dedicato.
 * Ritorna la MessageInfo aggiornata (DTO risolto lato server con
 * labelTranslated/className eventualmente ricalcolati). Non c'e' extraReducer
 * in messageSlice: il MessageInbox patcha l'elemento nel proprio local state
 * (vedi handleInfoUpdated, dentro Message.extractorInfos editabili).
 */
export const updateMessageInfo = createAsyncThunk<
  MessageInfo,
  { messageId: number; messageInfoId: number; payload: MessageInfoUpdatePayload }
>('messages/updateInfo', async ({ messageId, messageInfoId, payload }) => {
  return messageInfoApi.update(messageId, messageInfoId, payload);
});

// Placeholder hardcoded: nessun endpoint backend per l'analisi AI.
// La UI continua a funzionare leggendo da mockAiAnalyses; quando sara'
// disponibile un endpoint reale, sostituire il body con una chiamata HTTP
// dedicata (es. POST /message/{id}/analyze) e rimettere l'handler MSW.
export const analyzeMessage = createAsyncThunk<
  { suggestedNextStep: string },
  number
>(
  'messages/analyze',
  async (messageId) => {
    return mockAiAnalyses[messageId] ?? { suggestedNextStep: '' };
  },
);
